import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardBody, Col, Form, Input, Label, Row, Spinner, FormGroup } from 'reactstrap';
import { toast } from 'react-toastify';
import { Modulo } from '../../../Types/Tables.type';
import { get, postJson, putJson } from '../../../hook/request';
import { Btn } from '../../../AbstractElements';
import { buildTreeOptions } from '../../../Helpers';
import { Link } from 'react-router-dom';
import Loader from '../../../Components/Widgets/Loader/generic';


type FormState = {
    codice: string;
    classe: string;
    nome: string;
    ordine: number;
    abilitato: 1 | 0;
};


export default function LinguaForm() {
    const { slug } = useParams<{ slug: string }>();
    const isEdit = useMemo(() => !!slug && slug !== 'nuovo', [slug]);

    const [parentList, setParentList] = useState<Modulo[]>([]);
    const [loading, setLoading] = useState<boolean>(isEdit);
    const busy = loading;

    const [form, setForm] = useState<FormState>({
        codice: '',
        classe: '',
        nome: '',
        ordine: 0,
        abilitato: 1,
    });


    // Carico dettaglio se in edit
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!isEdit) return;
            try {
                setLoading(true);
                const res = await get<unknown>(`/languages/${encodeURIComponent(String(slug))}`);
                const raw = (res as any)?.data ?? res;
                const data = Array.isArray(raw) ? raw[0] : raw;

                if (!mounted) return;
                setForm({
                    codice: data?.codice ?? '',
                    classe: data?.classe ?? '',
                    nome: data?.nome ?? data?.nome_it ?? '',
                    ordine: data?.ordine ?? 0,
                    abilitato: data?.abilitato === 1 || data?.abilitato === '1' ? 1 : 0,
                });
            } catch (err: any) {
                const msg = err?.response?.data?.message ?? err?.message ?? 'Errore nel caricamento';
                toast.error(msg);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [isEdit, slug]);


    // ---- HANDLERS ----
    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : value,
        }));
    };


    // ---- OPZIONI AD ALBERO PER IL SELECT ----
    const treeOptions = useMemo(
        () =>
            buildTreeOptions(parentList as any, {
                excludeId: isEdit ? slug : undefined,
                indent: '\u00A0\u00A0',
            }),
        [parentList, isEdit, slug]
    );


    // ---- UI ----

    if (busy) {
        return (
            <div className="page-body pt-4">
                <Col xl={12} className="mb-lg-3 d-flex justify-content-end">
                    <Link to="/admin/moduli">
                        <Btn className="px-4" color="primary" type="button">
                            Torna Indietro
                        </Btn>
                    </Link>
                </Col>

                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody className="d-flex justify-content-center">
                            <Loader /> {/* ← qui il tuo componente loader */}
                        </CardBody>
                    </Card>
                </Col>
            </div>
        );
    }


    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const user_email = localStorage.getItem('email');
        const payload = {
            id: slug,
            codice: form.codice,
            classe: form.classe,
            nome: form.nome,
            ordine: form.ordine,
            abilitato: form.abilitato === 1 ? 1 : 0,
            user_email,
        };

        try {
            if (isEdit) {
                await putJson(`/languages/${encodeURIComponent(String(slug))}`, payload);
                toast.success('Lingua aggiornata!');
            } else {
                await postJson('/languages/insert', payload);
                toast.success('Lingua salvata!');
                setForm({
                    codice: '',
                    classe: '',
                    nome: '',
                    ordine: 0,
                    abilitato: 1,
                });
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Errore di rete';
            toast.error(`Errore salvataggio: ${msg}`);
        }
    };

    return (
        <div className="page-body pt-4">
            <Col xl={12} className="mb-lg-3 d-flex justify-content-end">
                <Link to="/admin/language">
                    <Btn className="px-4" color="primary" type="button">
                        Torna Indietro
                    </Btn>
                </Link>
            </Col>
            <Col xl={12} className="mb-lg-3">
                <Card>
                    <CardBody className="custom-input form-validation">
                        <Form onSubmit={handleSubmit}>
                            <Row className="g-3">

                                <Col xs={12}>
                                    <Label htmlFor="nome">Nome</Label>
                                    <Input
                                        id="nome"
                                        type="text"
                                        placeholder="nome"
                                        name="nome"
                                        value={form.nome}
                                        onChange={onChange}
                                        required
                                        disabled={loading}
                                    />
                                </Col>

                                <Col xs={12}>
                                    <Label htmlFor="codice">Codice</Label>
                                    <Input
                                        id="codice"
                                        type="text"
                                        placeholder="codice"
                                        name="codice"
                                        value={form.codice}
                                        onChange={onChange}
                                        required
                                        disabled={loading}
                                    />
                                </Col>

                                <Col xs={12}>
                                    <Label htmlFor="classe">Classe</Label>
                                    <Input
                                        id="classe"
                                        type="text"
                                        placeholder="classe"
                                        name="classe"
                                        value={form.classe}
                                        onChange={onChange}
                                        required
                                        disabled={loading}
                                    />
                                </Col>
                                <Col xs={12}>
                                    <Label htmlFor="classe">Ordine</Label>
                                    <Input
                                        id="ordine"
                                        type="number"
                                        placeholder="ordine"
                                        name="ordine"
                                        value={form.ordine}
                                        onChange={onChange}
                                        required
                                        disabled={loading}
                                    />
                                </Col>
                                <Col xs={12}>
                                    <div className="card-wrapper border rounded-3 checkbox-checked">
                                        <Label>Abilitato</Label>
                                        <div className="radio-form">
                                            <FormGroup check>
                                                <Input
                                                    type="radio"
                                                    id="abilitato-si"
                                                    name="abilitato"
                                                    value="1"
                                                    checked={form.abilitato === 1}
                                                    onChange={() => setForm(prev => ({ ...prev, abilitato: 1 }))}
                                                />
                                                <Label htmlFor="abilitato-si" check>{'Sì'}</Label>
                                            </FormGroup>

                                            <FormGroup check>
                                                <Input
                                                    type="radio"
                                                    id="abilitato-no"
                                                    name="abilitato"
                                                    value="0"
                                                    checked={form.abilitato === 0}
                                                    onChange={() => setForm(prev => ({ ...prev, abilitato: 0 }))}
                                                />
                                                <Label htmlFor="abilitato-no" check>{'No'}</Label>
                                            </FormGroup>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Col xs={12} className="mt-3 d-flex align-items-center gap-2">
                                <Btn className="px-4" color="primary" type="submit" disabled={loading}>
                                    {isEdit ? 'Aggiorna' : 'Crea'}
                                </Btn>
                                {loading && <Spinner size="sm" />}
                            </Col>
                        </Form>
                    </CardBody>
                </Card>
            </Col>
        </div>
    );
}
